import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Badge, Button, Card, Checkbox, EmptyState, Field, Input, LoadingScreen, Modal, PageHeader } from '../components/Ui';
import { useToast } from '../contexts/ToastContext';
import { useBusinessBranches, useResetBusinessBranchCorrelation, useUpdateBusinessBranch } from '../hooks/businessData';
import { currentPosition } from '../lib/geo';

interface Branch {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  allowedRadiusM: number;
  requireGeolocation: boolean;
  whatsapp: string | null;
  correlationCurrent: number;
  correlationCycle: number;
  active: boolean;
}

const initialForm = {
  name: '', address: '', lat: '', lng: '', allowedRadiusM: '',
  requireGeolocation: true, whatsapp: '', active: true, receptionPin: '', kitchenPin: '', deliveryPin: '',
};

export default function BranchesPage() {
  const { show } = useToast();
  const { data: branches = [], isLoading, isError, error } = useBusinessBranches();
  const updateBranchMutation = useUpdateBusinessBranch();
  const resetBranchMutation = useResetBusinessBranchCorrelation();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isError) {
      show(error instanceof Error ? error.message : 'No se pudieron cargar las sucursales.', 'error');
    }
  }, [error, isError, show]);

  const edit = (branch: Branch) => {
    setEditingId(branch.id);
    setForm({
      name: branch.name, address: branch.address, lat: String(branch.lat), lng: String(branch.lng),
      allowedRadiusM: String(branch.allowedRadiusM), requireGeolocation: branch.requireGeolocation,
      whatsapp: branch.whatsapp || '', active: branch.active, receptionPin: '', kitchenPin: '', deliveryPin: '',
    });
    setOpen(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingId) { show('Solo el administrador puede crear sucursales.', 'error'); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name, address: form.address, lat: Number(form.lat), lng: Number(form.lng),
        allowedRadiusM: Number(form.allowedRadiusM), requireGeolocation: form.requireGeolocation,
        whatsapp: form.whatsapp || null, active: form.active,
        receptionPin: form.receptionPin || undefined, kitchenPin: form.kitchenPin || undefined,
        deliveryPin: form.deliveryPin || undefined,
      };
      if (editingId) {
        await updateBranchMutation.mutateAsync({ id: editingId, payload });
      }
      setOpen(false); show(editingId ? 'Sucursal actualizada.' : 'Sucursal creada.', 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo guardar la sucursal.', 'error'); }
    finally { setBusy(false); }
  };

  const reset = async (branch: Branch) => {
    const confirmation = window.prompt(`Esto reiniciará #${branch.correlationCurrent} a #00 y abrirá un nuevo ciclo. Escribe REINICIAR:`);
    if (confirmation !== 'REINICIAR') return;
    try {
      await resetBranchMutation.mutateAsync({ id: branch.id, confirmation });
      show('Correlativo reiniciado.', 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo reiniciar.', 'error'); }
  };

  const useMyLocation = async () => {
    try {
      const position = await currentPosition();
      setForm((current) => ({ ...current, lat: String(position.coords.latitude), lng: String(position.coords.longitude) }));
      show('Coordenadas actualizadas.', 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo obtener la ubicación.', 'error'); }
  };

  if (isLoading) return <LoadingScreen />;
  return (
    <div>
      <PageHeader title="Sucursales" description="Puedes ver y editar las sucursales. La creación queda reservada al administrador." />
      {branches.length ? <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">{branches.map((branch) => (
        <Card key={branch.id}>
          <div className="flex items-start justify-between gap-3"><div><h2 className="text-lg font-black">{branch.name}</h2><p className="mt-1 text-sm text-slate-500">{branch.address || 'Sin dirección'}</p></div><Badge tone={branch.active ? 'green' : 'slate'}>{branch.active ? 'activa' : 'inactiva'}</Badge></div>
          <div className="mt-5 grid grid-cols-2 gap-3 rounded-2xl bg-slate-50 p-4 text-sm"><div><div className="text-xs text-slate-500">Correlativo</div><div className="mt-1 text-2xl font-black">#{String(branch.correlationCurrent).padStart(2, '0')}</div></div><div><div className="text-xs text-slate-500">Ciclo</div><div className="mt-1 text-2xl font-black">{branch.correlationCycle}</div></div><div><div className="text-xs text-slate-500">Radio</div><strong>{branch.allowedRadiusM} m</strong></div><div><div className="text-xs text-slate-500">GPS</div><strong>{branch.requireGeolocation ? 'Obligatorio' : 'Desactivado'}</strong></div></div>
          <div className="mt-4 flex flex-wrap gap-2"><Button variant="secondary" onClick={() => edit(branch)}>Editar</Button><Button variant="ghost" onClick={() => void reset(branch)}>Reiniciar correlativo</Button></div>
        </Card>
      ))}</div> : <EmptyState title="Sin sucursales" description="El administrador debe crear la primera sucursal para comenzar a recibir pedidos." />}

      <Modal open={open} onClose={() => setOpen(false)} title="Editar sucursal">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Nombre"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej. Sede Centro" /></Field>
          <Field label="WhatsApp"><Input pattern="\+?[0-9]{7,15}" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+584121234567" /></Field>
          <Field label="Dirección"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Ej. Av. Principal, local 12" /></Field>
          <Field label="Radio permitido (m)"><Input type="number" min="20" max="10000" required value={form.allowedRadiusM} onChange={(e) => setForm({ ...form, allowedRadiusM: e.target.value })} placeholder="Ej. 150" /></Field>
          <Field label="Latitud"><Input type="number" step="any" required value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="Ej. 10.4806" /></Field>
          <Field label="Longitud"><Input type="number" step="any" required value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="Ej. -66.9036" /></Field>
          <div className="sm:col-span-2"><Button type="button" variant="secondary" onClick={() => void useMyLocation()}>⌖ Usar mi ubicación actual</Button></div>
          <Field label="Nuevo PIN recepción (opcional)"><Input pattern="[0-9]{4}" maxLength={4} value={form.receptionPin} onChange={(e) => setForm({ ...form, receptionPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="4 dígitos" /></Field>
          <Field label="Nuevo PIN cocina (opcional)"><Input pattern="[0-9]{4}" maxLength={4} value={form.kitchenPin} onChange={(e) => setForm({ ...form, kitchenPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="4 dígitos" /></Field>
          <Field label="Nuevo PIN reparto (opcional)"><Input pattern="[0-9]{4}" maxLength={4} value={form.deliveryPin} onChange={(e) => setForm({ ...form, deliveryPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="4 dígitos" /></Field>
          <Checkbox label="Exigir ubicación al empleado" isSelected={form.requireGeolocation} onChange={(requireGeolocation) => setForm({ ...form, requireGeolocation })} className="rounded-xl border p-3 text-sm font-semibold" />
          <Checkbox label="Sucursal activa" isSelected={form.active} onChange={(active) => setForm({ ...form, active })} className="rounded-xl border p-3 text-sm font-semibold" />
          <div className="flex justify-end gap-2 sm:col-span-2"><Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button><Button type="submit" busy={busy}>Guardar</Button></div>
        </form>
      </Modal>
    </div>
  );
}
